import React, { useEffect, useState, useRef, createElement } from 'react';
import { XIcon, HomeIcon, Loader2Icon, BookOpenIcon, DownloadIcon, ShareIcon, CheckIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoadingBunny } from './LoadingBunny';
type ViewerState = 'loading' | 'error' | 'viewing';
type BookGenerationStatus = 'initial_generation' | 'retrying_initial_generation' | 'subsequent_generation' | 'failed_initial_generation' | 'incomplete' | 'complete';
interface BookGenerationStatuses {
  [key: string]: BookGenerationStatus;
}
export const StoryViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewerState, setViewerState] = useState<ViewerState>('loading');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(-1);
  const [currentPageImage, setCurrentPageImage] = useState<string | null>(null);
  const [currentPageText, setCurrentPageText] = useState<string | null>(null);
  const [isOldBook, setIsOldBook] = useState(false);
  const [showStartButton, setShowStartButton] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [reachedPageLimit, setReachedPageLimit] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showLoginSuccessDialog, setShowLoginSuccessDialog] = useState(false);
  const [moderationError, setModerationError] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showEndPage, setShowEndPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [eventMessages, setEventMessages] = useState<string[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const bookId = new URLSearchParams(location.search).get('book_id');
  const isLoggedIn = !!localStorage.getItem('access_token') && !!localStorage.getItem('user_email');
  const [shareSuccess, setShareSuccess] = useState(false);
  const fetchPage = () => {
    if (!bookId) return;
    if (isOldBook) {
      // Use original single image format for old books
      const singleImageUrl = `https://bot.messagesimproved.com/storybunny_pngs/${bookId}_${currentPage}.png`;
      setCurrentPageImage(singleImageUrl);
      setCurrentPageText(null);
    } else {
      // Use new dual image format for new books
      const imageUrl = `https://bot.messagesimproved.com/storybunny_png_images/${bookId}_${currentPage}.png`;
      const textUrl = `https://bot.messagesimproved.com/storybunny_png_texts/${bookId}_${currentPage}.png`;
      setCurrentPageImage(imageUrl);
      setCurrentPageText(textUrl);
    }
  };
  const updateBookGenerationStatus = (bookId: string, status: BookGenerationStatus) => {
    const statuses = JSON.parse(localStorage.getItem('book_generation_status') || '{}') as BookGenerationStatuses;
    statuses[bookId] = status;
    localStorage.setItem('book_generation_status', JSON.stringify(statuses));
  };
  const getBookGenerationStatus = (bookId: string): BookGenerationStatus => {
    const statuses = JSON.parse(localStorage.getItem('book_generation_status') || '{}') as BookGenerationStatuses;
    return statuses[bookId] || 'initial_generation';
  };
  const setupPageStatusListener = () => {
    if (!bookId) {
      setError('No story ID found');
      setViewerState('error');
      return;
    }
    try {
      // Set initial status if not already set
      if (!getBookGenerationStatus(bookId)) {
        updateBookGenerationStatus(bookId, 'initial_generation');
      }
      const source = new EventSource(`https://bot.messagesimproved.com/get_storybook_status?book_id=${bookId}`, {
        withCredentials: true // Enable credentials for CORS
      });
      // Set up error handler first to catch connection errors
      source.onerror = err => {
        console.error('EventSource error:', err);
        // Check if the error is due to CORS or network
        if (source.readyState === EventSource.CLOSED) {
          setError('Unable to connect to story service. Please try refreshing the page.');
          updateBookGenerationStatus(bookId, 'failed_initial_generation');
        } else {
          setError('Your story was filtered by moderation. Please try a new story with different content.');
          updateBookGenerationStatus(bookId, 'failed_initial_generation');
        }
        setViewerState('error');
        source.close();
      };
      source.onopen = () => {
        console.log('EventSource connection established');
      };
      source.onmessage = (event: MessageEvent) => {
        try {
          const chunk = event.data.trim();
          if (!chunk || chunk === 'ping') return;
          // Safely update state
          setEventMessages(prev => [...prev, chunk]);
          if (chunk === 'old_book') {
            setIsOldBook(true);
          } else if (chunk === 'complete') {
            source.close();
            setIsComplete(true);
            setViewerState('viewing');
            updateBookGenerationStatus(bookId, 'complete');
          } else if (chunk === 'moderation_error') {
            source.close();
            setError('Your story was filtered by moderation. Please try a new story with different content.');
            setViewerState('error');
            updateBookGenerationStatus(bookId, 'failed_initial_generation');
          } else if (chunk === 'reached_page_limit') {
            setReachedPageLimit(true);
            updateBookGenerationStatus(bookId, 'incomplete');
          } else if (chunk === 'logged_in') {
            setShowLoginSuccessDialog(true);
            setReachedPageLimit(false);
            updateBookGenerationStatus(bookId, 'subsequent_generation');
          } else if (chunk.startsWith('page|')) {
            const pageCount = parseInt(chunk.split('|')[1], 10);
            if (!isNaN(pageCount) && pageCount > totalPages) {
              setTotalPages(pageCount);
              setShowStartButton(true);
              setViewerState('viewing');
            }
          }
        } catch (err) {
          console.error('Error processing message:', err);
          setError('Error processing story data');
          setViewerState('error');
          updateBookGenerationStatus(bookId, 'failed_initial_generation');
          source.close();
        }
      };
      eventSourceRef.current = source;
      // Cleanup function
      return () => {
        if (source) {
          source.close();
          console.log('EventSource connection closed');
        }
      };
    } catch (err) {
      console.error('Error setting up EventSource:', err);
      setError('Unable to connect to story service');
      setViewerState('error');
      if (bookId) {
        updateBookGenerationStatus(bookId, 'failed_initial_generation');
      }
    }
  };
  const startViewing = () => {
    setHasStarted(true);
    fetchPage();
  };
  const goToNextPage = () => {
    if (currentPage === totalPages && isComplete) {
      setShowEndPage(true);
    } else if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    } else if (currentPage === totalPages && reachedPageLimit) {
      setShowLoginDialog(true);
    }
  };
  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };
  const openSignupPage = () => {
    // Pass both book_id and current page to the login/signup page
    navigate(`/create-account?book_id=${bookId}&page=${currentPage}`);
  };
  const handleShare = async () => {
    try {
      const shareText = 'Check out my story on Story Bunny: ' + window.location.href;
      // Check if Web Share API is available and supported
      if (navigator.share && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: 'Check out my story!',
            text: 'Check out my story on Story Bunny!',
            url: window.location.href
          });
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
        } catch (shareError) {
          console.error('Web Share API error:', shareError);
          // If user cancelled sharing, don't show error
          if (shareError instanceof Error && shareError.name === 'AbortError') {
            return;
          }
          // Fall back to clipboard if Web Share fails for other reasons
          await copyToClipboard();
        }
      } else {
        // Fallback for browsers that don't support the Web Share API
        await copyToClipboard();
      }
    } catch (error) {
      console.error('Error in sharing process:', error);
      alert('There was an error sharing your story. Please try again.');
    }
  };
  // Separate clipboard function for better error handling
  const copyToClipboard = async () => {
    try {
      const shareText = 'Check out my story on Story Bunny: ' + window.location.href;
      // Check if Clipboard API is available
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(shareText);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 3000);
        alert('Link copied to clipboard! Paste it to share your story.');
      } else {
        // Fallback for older browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        textArea.style.position = 'fixed'; // Avoid scrolling to bottom
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
          alert('Link copied to clipboard! Paste it to share your story.');
        } else {
          throw new Error('Clipboard copy failed');
        }
      }
    } catch (clipboardError) {
      console.error('Clipboard error:', clipboardError);
      alert('Unable to copy link. Please copy the URL from your address bar manually.');
    }
  };
  useEffect(() => {
    if (bookId) setupPageStatusListener();
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [bookId]);
  useEffect(() => {
    if (hasStarted) fetchPage();
  }, [currentPage, isOldBook]);
  useEffect(() => {
    const incrementReadCount = async () => {
      if (!bookId) return;
      try {
        const response = await fetch('https://bot.messagesimproved.com/increment_storybook_read_count', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            book_id: bookId
          })
        });
        if (!response.ok) {
          throw new Error('Failed to increment read count');
        }
        const data = await response.json();
        if (data.status !== 'Successfully incremented read count') {
          console.warn('Unexpected response from read count increment');
        }
      } catch (error) {
        // Silently handle error - don't impact user experience
        console.error('Error incrementing read count:', error);
      }
    };
    // Only call when reaching the end page
    if (showEndPage) {
      incrementReadCount();
    }
  }, [showEndPage, bookId]); // Only run when showEndPage changes
  if (viewerState === 'loading') {
    return <LoadingBunny skipProgress={showStartButton} onProgressComplete={() => setViewerState('viewing')} messages={eventMessages} data-id="element-1045" />;
  }
  if (viewerState === 'error') {
    return <div className="flex-1 flex items-center justify-center p-4 bg-[#FFFBF6]" data-id="element-1046">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg" data-id="element-1047">
          <h2 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-1048">
            Oops! We couldn't write your story
          </h2>
          <p className="text-[#6B4B3E]/75 mb-8" data-id="element-1049">
            {error || 'Your story was filtered by moderation. Please try a new story with different content.'}
          </p>
          <Link to="/" className="block w-full px-6 py-3 border-2 border-[#E5C1A9] text-[#6B4B3E] rounded-full hover:bg-[#FFF0E6] transition-colors" data-id="element-1050">
            Return Home
          </Link>
        </div>
      </div>;
  }
  return <div className="flex-1 flex flex-col bg-[#FFFBF6]" data-id="element-1051">
      {!showStartButton && !moderationError && <LoadingBunny data-id="element-1052" />}
      {moderationError ? <div className="flex-1 flex items-center justify-center p-4" data-id="element-1053">
          <div className="bg-[#FFFAE4] rounded-xl p-8 max-w-md w-full text-center shadow-lg border border-[#E5C1A9]" data-id="element-1054">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-1055">
              Content Warning
            </h2>
            <p className="text-[#6B4B3E]/75" data-id="element-1056">
              This story was caught by our moderation filter. Please try
              creating a new story with different content.
            </p>
          </div>
        </div> : showEndPage ? <div className="flex-1 flex items-center justify-center p-4" data-id="element-1057">
          <div className="max-w-2xl w-full text-center space-y-8" data-id="element-1058">
            <h1 className="text-6xl font-bold text-[#6B4B3E] mb-4" data-id="element-1059">
              All Done!
            </h1>
            <p className="text-2xl text-[#6B4B3E]/75 mb-8" data-id="element-1060">
              {isLoggedIn ? 'Hooray you finished your story!' : <p className="text-2xl text-[#6B4B3E]/75 mb-8" data-id="element-1061">
                  🔐 Want to save your story forever? Create an account to keep
                  it, download it, and come back to make more.
                </p>}
            </p>
            {isLoggedIn ? <div className="space-y-4" data-id="element-1062">
                <a href={`https://bot.messagesimproved.com/storybunny_complete_book_pdfs/${bookId}.pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-[#E5C1A9] text-[#6B4B3E] rounded-full hover:bg-[#D4A088] hover:text-white transition-colors text-xl mx-4" data-id="element-1063">
                  <DownloadIcon size={24} className="mr-2" data-id="element-1064" />
                  Download Book
                </a>
                <button onClick={() => navigate('/garden')} className="inline-flex items-center px-8 py-4 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors text-xl" data-id="element-1065">
                  <HomeIcon size={24} className="mr-2" data-id="element-1066" />
                  Return to Story Garden
                </button>
              </div> : <div className="space-y-4" data-id="element-1067">
                <Link to="/create-account" className="inline-block px-8 py-4 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors text-xl" data-id="element-1068">
                  Sign In To Save & Download
                </Link>
                <p className="text-[#6B4B3E]/75 text-lg" data-id="element-1069">
                  It's free to create an account using an email address. Or{' '}
                  <Link to="/login" className="text-[#D4A088] hover:underline" data-id="element-1070">
                    log in
                  </Link>{' '}
                  if you already have an account.
                </p>
                {/* Moved share button to bottom */}
                <div className="flex justify-center mt-8" data-id="element-1071">
                  <button onClick={handleShare} className="inline-flex items-center px-6 py-3 bg-[#E5C1A9] text-[#6B4B3E] rounded-full hover:bg-[#D4A088] transition-colors text-xl" data-id="element-1072">
                    {shareSuccess ? <>
                        <CheckIcon size={24} className="mr-2" data-id="element-1073" />
                        Link has been copied to your clipboard.
                      </> : <>
                        <ShareIcon size={24} className="mr-2" data-id="element-1074" />
                        Share Your Story
                      </>}
                  </button>
                </div>
              </div>}
          </div>
        </div> : <div className="flex-1 flex justify-center items-center p-4" data-id="element-1075">
          <div className="w-full max-w-4xl bg-[#FFFAE4] rounded-xl shadow-lg border border-[#E5C1A9]" data-id="element-1076">
            <div className="p-8" data-id="element-1077">
              <div className="text-center" data-id="element-1078">
                {hasStarted && currentPageImage && <div className="relative" data-id="element-1079">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-center" data-id="element-1080">
                      <img src={currentPageImage} alt={`Page ${currentPage} ${isOldBook ? 'content' : 'illustration'}`} className="w-full max-w-xs h-auto rounded-lg" data-id="element-1081" />
                      {!isOldBook && currentPageText && <img src={currentPageText} alt={`Page ${currentPage} text`} className="w-full max-w-xs h-auto rounded-lg" data-id="element-1082" />}
                    </div>
                    {/* Mobile navigation overlay */}
                    {hasStarted && !isOldBook && currentPageText && <div className="md:hidden absolute bottom-0 left-0 right-0 bg-[#FFFAE4] bg-opacity-80 backdrop-blur-sm rounded-b-lg" data-id="element-1083">
                        <div className="flex justify-between items-center gap-2 p-4" data-id="element-1084">
                          <button onClick={goToPreviousPage} disabled={currentPage === 0} className="px-6 py-2 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#FFFBF6]" data-id="element-1085">
                            <span className="text-lg" data-id="element-1086">&lt;</span>
                            <span className="sr-only" data-id="element-1087">Previous</span>
                          </button>
                          <span className="text-sm text-[#6B4B3E]/60 font-medium cursor-default" onDoubleClick={() => setShowDebugInfo(!showDebugInfo)} data-id="element-1088">
                            {currentPage + 1}
                            {showDebugInfo && <span className="ml-2 text-[#6B4B3E]/60" data-id="element-1089">
                                (Pages Generated: {totalPages + 1},{' '}
                                {isOldBook ? 'Old' : 'New'} Format)
                              </span>}
                          </span>
                          <button onClick={goToNextPage} disabled={!isComplete && currentPage >= totalPages && !reachedPageLimit} className="px-6 py-2 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#FFFBF6]" data-id="element-1090">
                            {!isComplete && currentPage >= totalPages && !reachedPageLimit ? <Loader2Icon size={20} className="animate-spin" data-id="element-1091" /> : currentPage === totalPages && isComplete ? 'Finish' : currentPage === totalPages && reachedPageLimit ? 'Sign In' : <span className="text-lg" data-id="element-1092">&gt;</span>}
                            <span className="sr-only" data-id="element-1093">Next</span>
                          </button>
                        </div>
                      </div>}
                  </div>}
                {!hasStarted && showStartButton && <div className="h-full flex items-center justify-center py-8" data-id="element-1094">
                    <button onClick={startViewing} className="px-8 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors text-xl font-medium flex items-center" data-id="element-1095">
                      <BookOpenIcon size={24} className="mr-2" data-id="element-1096" />
                      Start Reading
                    </button>
                  </div>}
              </div>
              {/* Desktop navigation - hidden on mobile when we have dual images */}
              {hasStarted && (isOldBook || !currentPageText) && <div className="flex justify-between items-center gap-2 mt-4" data-id="element-1097">
                  <button onClick={goToPreviousPage} disabled={currentPage === 0} className="px-6 py-2 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1098">
                    <span className="text-lg" data-id="element-1099">&lt;</span>
                    <span className="sr-only" data-id="element-1100">Previous</span>
                  </button>
                  <span className="text-sm text-[#6B4B3E]/60 font-medium cursor-default" onDoubleClick={() => setShowDebugInfo(!showDebugInfo)} data-id="element-1101">
                    {currentPage + 1}
                    {showDebugInfo && <span className="ml-2 text-[#6B4B3E]/60" data-id="element-1102">
                        (Pages Generated: {totalPages + 1},{' '}
                        {isOldBook ? 'Old' : 'New'} Format)
                      </span>}
                  </span>
                  <button onClick={goToNextPage} disabled={!isComplete && currentPage >= totalPages && !reachedPageLimit} className="px-6 py-2 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1103">
                    {!isComplete && currentPage >= totalPages && !reachedPageLimit ? <Loader2Icon size={20} className="animate-spin" data-id="element-1104" /> : currentPage === totalPages && isComplete ? 'Finish' : currentPage === totalPages && reachedPageLimit ? 'Sign In' : <span className="text-lg" data-id="element-1105">&gt;</span>}
                    <span className="sr-only" data-id="element-1106">Next</span>
                  </button>
                </div>}
              {/* Desktop navigation for dual images - always visible on desktop */}
              {hasStarted && !isOldBook && currentPageText && <div className="hidden md:flex justify-between items-center gap-2 mt-4" data-id="element-1107">
                  <button onClick={goToPreviousPage} disabled={currentPage === 0} className="px-6 py-2 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1108">
                    <span className="text-lg" data-id="element-1109">&lt;</span>
                    <span className="sr-only" data-id="element-1110">Previous</span>
                  </button>
                  <span className="text-sm text-[#6B4B3E]/60 font-medium cursor-default" onDoubleClick={() => setShowDebugInfo(!showDebugInfo)} data-id="element-1111">
                    {currentPage + 1}
                    {showDebugInfo && <span className="ml-2 text-[#6B4B3E]/60" data-id="element-1112">
                        (Pages Generated: {totalPages + 1},{' '}
                        {isOldBook ? 'Old' : 'New'} Format)
                      </span>}
                  </span>
                  <button onClick={goToNextPage} disabled={!isComplete && currentPage >= totalPages && !reachedPageLimit} className="px-6 py-2 text-[#6B4B3E] border-2 border-[#E5C1A9] rounded-full hover:bg-[#FFF0E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1113">
                    {!isComplete && currentPage >= totalPages && !reachedPageLimit ? <Loader2Icon size={20} className="animate-spin" data-id="element-1114" /> : currentPage === totalPages && isComplete ? 'Finish' : currentPage === totalPages && reachedPageLimit ? 'Sign In' : <span className="text-lg" data-id="element-1115">&gt;</span>}
                    <span className="sr-only" data-id="element-1116">Next</span>
                  </button>
                </div>}
            </div>
          </div>
        </div>}
      {showLoginDialog && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1117">
          <div className="bg-[#FFFAE4] rounded-xl p-6 max-w-md w-full border border-[#E5C1A9]" data-id="element-1118">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-1119">Sign In</h2>
            <p className="text-[#6B4B3E] mb-6" data-id="element-1120">
              Sign in to see what happens next!
            </p>
            <div className="flex justify-end space-x-4" data-id="element-1121">
              <button onClick={() => setShowLoginDialog(false)} className="px-4 py-2 text-[#6B4B3E] hover:bg-[#FFF0E6] rounded-full transition-colors" data-id="element-1122">
                Close
              </button>
              <button onClick={openSignupPage} className="px-6 py-2 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors" data-id="element-1123">
                Sign In
              </button>
            </div>
          </div>
        </div>}
      {showLoginSuccessDialog && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1124">
          <div className="bg-[#FFFAE4] rounded-xl p-6 max-w-md w-full border border-[#E5C1A9]" data-id="element-1125">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-1126">
              Thank you for signing up!
            </h2>
            <p className="text-[#6B4B3E] mb-6" data-id="element-1127">
              We're writing the rest of your story now...
            </p>
            <div className="flex justify-end" data-id="element-1128">
              <button onClick={() => setShowLoginSuccessDialog(false)} className="px-4 py-2 text-[#6B4B3E] hover:bg-[#FFF0E6] rounded-full transition-colors" data-id="element-1129">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
};